import React, { useState, useEffect, useCallback, useMemo } from 'react';
import '../Css/Order.css';
import '../Css/OrderExcel.css';
import '../Css/Summary.css';
import { Link } from 'react-router-dom';
import { Tooltip } from 'react-tooltip';
import Navbar from './Navbar';
import DatePicker from 'react-datepicker';
import { Typography, CircularProgress } from '@mui/material';
import 'react-datepicker/dist/react-datepicker.css';
import { MdDownloadForOffline } from "react-icons/md";
import Logger from './Logger';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import { FaListUl } from "react-icons/fa";
import { getLoggerBaseURL } from '../utils/getLoggerBaseURL';

const Summary = () => {
    const userEmail = sessionStorage.getItem("userEmail");
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedDate, setSelectedDate] = useState(new Date());

    // Initialize Logger
    const logger = useMemo(() => new Logger(getLoggerBaseURL()), []);

    const fetchData = useCallback(async () => {
        setLoading(true);
        setError(null);

        try {
            const response = await fetch(`/api/driver/summary/${selectedDate.toISOString()}/${userEmail}`);

            if (response.ok) {
                const responseData = await response.json();

                if (!responseData || responseData.length === 0) {
                    logger.log('No data available for the selected date', 'INFO');
                    setData([]);
                    setLoading(false);
                    return;
                }

                setData(responseData);
            } else {
                logger.log('Failed to fetch data for Order Excel file', 'ERROR');
                setError('Failed to fetch data');
            }
        } catch (error) {
            logger.log(`Error fetching data: ${error.message}`, 'ERROR');
            setError('Error fetching data');
        }

        setLoading(false);
    }, [selectedDate, userEmail, logger]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    function generateUniqueId(dateString, driverSerialNo) {
        const date = new Date(dateString);  // Convert the string to a Date object
        if (isNaN(date)) {
            return null;
        }
        const day = date.getDate().toString().padStart(2, '0');
        const month = (date.getMonth() + 1).toString().padStart(2, '0'); // Months are 0-indexed
        const year = date.getFullYear().toString().slice(-2);
        return `${day}${month}${year}${driverSerialNo}`;
    }

    const handleDownloadPdf = () => {
        const doc = new jsPDF('landscape');

        const date = new Date(selectedDate);
        const day = date.getDate().toString().padStart(2, '0');
        const month = (date.getMonth() + 1).toString().padStart(2, '0'); // Months are 0-indexed
        const year = date.getFullYear().toString().slice(-2);

        const pageWidth = doc.internal.pageSize.getWidth();
        const pageHeight = doc.internal.pageSize.getHeight();
        const borderPadding = 10;

        // Set Title
        const title = "Zordigo";
        const fontSize = 14;
        const titleWidth = doc.getTextWidth(title);
        doc.setFontSize(fontSize);
        doc.text(title, (pageWidth - titleWidth) / 2, 20);

        // Draw border around page
        doc.rect(borderPadding, borderPadding, pageWidth - 2 * borderPadding, pageHeight - 2 * borderPadding);

        // Add Date and Summary Report on the left side
        const leftMargin = 20; // Margin from the left side
        const topMargin = 30; // Margin from the top for the summary section
        doc.setFontSize(12);
        doc.text(`Date: ${day}-${month}-${year}`, leftMargin, topMargin);
        doc.text("Summary Report", 130, topMargin + 10);

        // Prepare Table Data
        const tableColumn = ["Report ID", "Driver Name", "Failed No.", "Delivered No.", "Cash", "UPI", "Cheque", "Stock Return", "Round-off"];
        const tableRows = [];
        data.forEach(item => {
            const rowData = [
                generateUniqueId(selectedDate.toISOString(), item.serialNumber),
                item.driverName,
                item.failedOrders || 0,
                item.completedOrders || 0,
                item.totalCashAmount,
                item.totalUpiAmount,
                item.totalChequeAmount,
                item.totalStockAmount,
                item.totalRoundAmount
            ];
            tableRows.push(rowData);
        });

        // Add Total Row to the Table
        const totalRow = [
            '',
            'Total',
            totalFailedOrders,
            totalCompletedOrders,
            totalCashAmount,
            totalUpiAmount,
            totalChequeAmount,
            totalStockAmount,
            totalRoundAmount
        ];
        tableRows.push(totalRow);

        // Generate the Table
        doc.autoTable({
            head: [tableColumn],
            body: tableRows,
            startY: topMargin + 20, // Start below the summary section
            headStyles: { fillColor: '#07a6ef' }, // Customize header color
            margin: { top: topMargin + 20 }, // Adjust margin if needed
            didDrawPage: (data) => {
                // Add Date and Page Number in Footer
                const { pageNumber } = data;
                const footerText = `Page ${pageNumber} | Date: ${new Date().toLocaleDateString()}`;
                doc.setFontSize(10);
                doc.text(footerText, 14, doc.internal.pageSize.height - 10);
            },
            styles: {
                cellPadding: 2, // Padding inside each cell
                fontSize: 8, // Font size in cells
            },
            // Row height customization
            rowHeight: 10,
        });

        // Save the PDF
        doc.save(`Summary-${day}-${month}-${year}.pdf`);
    };

    const handleSearchChange = (e) => {
        setSearchTerm(e.target.value);
    };

    const handleDateChange = (date) => {
        setSelectedDate(date);
    };

    const filteredData = data.filter(item =>
        item.driverName?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const totalFailedOrders = filteredData.reduce((sum, item) => sum + (item.failedOrders || 0), 0);
    const totalCompletedOrders = filteredData.reduce((sum, item) => sum + (item.completedOrders || 0), 0);
    const totalCashAmount = filteredData.reduce((sum, item) => sum + (item.totalCashAmount || 0), 0);
    const totalUpiAmount = filteredData.reduce((sum, item) => sum + (item.totalUpiAmount || 0), 0);
    const totalChequeAmount = filteredData.reduce((sum, item) => sum + (item.totalChequeAmount || 0), 0);
    const totalStockAmount = filteredData.reduce((sum, item) => sum + (item.totalStockAmount || 0), 0);
    const totalRoundAmount = filteredData.reduce((sum, item) => sum + (item.totalRoundAmount || 0), 0);

    return (
        <>
            <div className='New summary-page'>
                <Navbar />
                <div className="summaryToolbar">
                    <h2 className="headS">
                        <Link to="/Reports" className="reports">Reports</Link>
                        <span className="crumbSep">{">"}</span>
                        <span className="reporter">Daily Summary</span>
                    </h2>
                    <div className="summaryFilters">
                        <input type="text" className="searchbarrs" placeholder="Search..." value={searchTerm} onChange={handleSearchChange} autoComplete="off" />
                        <DatePicker selected={selectedDate} onChange={handleDateChange} dateFormat="yyyy-MM-dd" className="dropdownO" />
                        <div className="uploadSS">
                            <button className="choose-file-buttonn" onClick={handleDownloadPdf} style={{ background: 'rgb(121, 198, 233)', border: 'none', cursor: 'pointer', padding: '6px' }} data-tooltip-id="download-tooltip">
                                <MdDownloadForOffline size={30} />
                                <Tooltip id="download-tooltip" place="bottom" content="Get PDF" />
                            </button>
                        </div>
                    </div>
                </div>
                {loading ? (
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '80vh' }}>
                        <CircularProgress />
                        <Typography variant="h6" style={{ marginTop: '20px' }}>Fetching Data...</Typography>
                    </div>
                ) : (
                    <table className='orderT'>
                        <thead>
                            <tr>
                                <th className='Oth'>Report ID</th>
                                <th className='Oth'>Driver Name</th>
                                <th className='Oth'>Failed No.</th>
                                <th className='Oth'>Delivered No.</th>
                                <th className='Oth'>Cash</th>
                                <th className='Oth'>UPI</th>
                                <th className='Oth'>Cheque</th>
                                <th className='Oth'>Stock Return</th>
                                <th className='Oth'>Round-off</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredData.length === 0 ? (
                                <tr>
                                    <td className="noOrders" colSpan="9">
                                        <div className="center-content">
                                            <div className="icon-wrapper">
                                                <FaListUl className="icon" />
                                            </div>
                                            <span className="noOrdersMsg">No data available</span>
                                        </div>
                                    </td>
                                </tr>
                            ) : (
                                filteredData.map((item, index) => (
                                    <tr key={item._id}>
                                        <td className='Otd'>{generateUniqueId(selectedDate.toISOString(), item.serialNumber)}</td>
                                        <td className='Otd'>{item.driverName}</td>
                                        <td className='Otd'>{item.failedOrders || 0}</td>
                                        <td className='Otd'>{item.completedOrders || 0}</td>
                                        <td className='Otd'>₹ {item.totalCashAmount}</td>
                                        <td className='Otd'>₹ {item.totalUpiAmount}</td>
                                        <td className='Otd'>₹ {item.totalChequeAmount}</td>
                                        <td className='Otd'>₹ {item.totalStockAmount}</td>
                                        <td className='Otd'>₹ {item.totalRoundAmount}</td>
                                    </tr>
                                ))
                            )}
                            <tr style={{ color: '#07a6ef' }}>
                                <td className='Otd'></td>
                                <td className='Otd'><strong>Total</strong></td>
                                <td className='Otd'><strong>{totalFailedOrders}</strong></td>
                                <td className='Otd'><strong>{totalCompletedOrders}</strong></td>
                                <td className='Otd'><strong>₹ {totalCashAmount}</strong></td>
                                <td className='Otd'><strong>₹ {totalUpiAmount}</strong></td>
                                <td className='Otd'><strong>₹ {totalChequeAmount}</strong></td>
                                <td className='Otd'><strong>₹ {totalStockAmount}</strong></td>
                                <td className='Otd'><strong>₹ {totalRoundAmount}</strong></td>
                            </tr>
                        </tbody>
                    </table>
                )}
                {error && (
                    <Typography color="error" style={{ textAlign: 'center', marginTop: '20px' }}>
                        {error}
                    </Typography>
                )}
            </div>
        </>
    );
};

export default Summary;
